// Theme classes for the shared UI package
// These are Tailwind CSS class strings that use CSS variables for theming

import { MemberTier, CorporatePartnerBranding } from './types';
import { isLightColor } from '@member-portal/utils';

// User types that determine theme selection
export const LIGHT_THEME_USER_TYPES: MemberTier[] = ['explorer', 'program'];

export const DARK_THEME_USER_TYPES: MemberTier[] = ['core', 'elevate', 'executive_circle', 'executive', 'prestige'];

export const getAllUserTypes = () => [
  ...LIGHT_THEME_USER_TYPES,
  ...DARK_THEME_USER_TYPES,
];

// Theme utility functions
export const getThemeClasses = (userType: string | null): 'light' | 'dark' => {
  if (!userType) return 'dark';
  const tier = normalizeMemberType(userType);
  return LIGHT_THEME_USER_TYPES.includes(tier) ? 'light' : 'dark';
};

export const shouldUseLightTheme = (userType: string | null): boolean => {
  return getThemeClasses(userType) === 'light';
};

export const isValidUserType = (userType: string): boolean => {
  return getAllUserTypes().includes(userType as MemberTier);
};

// Helper function to normalize member type strings to our standard format
export const normalizeMemberType = (memberType: string): MemberTier => {
  if (!memberType) return 'explorer';

  const normalized = memberType.toLowerCase();

  // Handle various formats that might come from the API
  // FHAN members get dark theme (same as core)
  if (normalized === 'fhan') return 'core';

  // Regular member types
  if (normalized.includes('explorer')) return 'explorer';
  if (normalized.includes('family')) return 'core';
  if (normalized.includes('elevate')) return 'elevate';
  // Check for "executive circle" before "executive" (corporate tier)
  if (normalized.includes('executive circle')) return 'executive_circle';
  if (normalized.includes('core') || normalized === 'four hundred') return 'core';

  // Corporate tiers
  if (normalized.includes('executive') || normalized === 'executive') return 'executive';
  if (normalized.includes('prestige') || normalized === 'prestige') return 'prestige';
  if (normalized.includes('program') || normalized === 'program') return 'program';

  // Default fallback
  return 'explorer';
};

// Shared layout utilities (same for both themes)
const sharedLayout = {
  container: 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8',
  grid: {
    cols1: 'grid grid-cols-1 gap-8',
    cols2: 'grid grid-cols-1 md:grid-cols-2 gap-8',
    cols3: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8',
    cols4: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8',
  },
};

// Base theme with all shared styles (no colors)
const baseTheme = {
  // Typography - base styles without colors
  title: {
    h1: 'font-title font-medium text-3xl md:text-5xl lg:text-7xl tracking-wider uppercase',
    h2: 'font-title font-medium text-2xl md:text-4xl lg:text-5xl tracking-wider uppercase',
    h3: 'font-title font-medium text-xl md:text-3xl lg:text-4xl tracking-wider uppercase',
    h4: 'font-title font-semibold text-lg md:text-2xl lg:text-3xl tracking-wide uppercase',
    h5: 'font-title font-semibold text-md md:text-xl lg:text-2xl tracking-wide uppercase',
  },
  text: {
    body: 'font-body font-medium leading-relaxed tracking-form',
    field: 'font-body font-semibold text-xs leading-normal tracking-widest uppercase',
    muted: 'font-body font-medium leading-normal tracking-form',
    mutedSmall: 'font-body font-medium text-sm leading-normal tracking-form',
    breadcrumb: 'font-body font-medium text-sm tracking-wider uppercase tracking-extra-wide',
    ultraWide: 'font-body font-medium leading-relaxed tracking-ultra-ultra-wide',
    primary: 'font-body font-medium leading-relaxed tracking-extra-wide',
    secondary: 'font-body font-medium leading-relaxed tracking-extra-wide',
    caption: 'font-body font-medium text-xs leading-relaxed tracking-form',
    button: 'font-body font-normal leading-relaxed tracking-extra-wide',
    sectionHeader: 'font-body font-medium text-lg uppercase tracking-wider leading-relaxed',
    pageTitle: 'font-body font-medium text-2xl leading-relaxed tracking-form',
    stepTitle: 'font-body font-medium text-base leading-relaxed tracking-form',
  },

  // Components - base styles without colors (matches Button component)
  button: {
    base: 'inline-flex items-center justify-center font-body font-medium leading-relaxed tracking-extra-wide uppercase transition-all duration-300 focus:outline-none',
    primary: 'inline-flex items-center justify-center font-body font-medium leading-relaxed tracking-extra-wide uppercase transition-all duration-300 focus:outline-none px-6 py-3 text-sm',
    secondary: 'inline-flex items-center justify-center font-body font-medium leading-relaxed tracking-extra-wide uppercase transition-all duration-300 focus:outline-none border px-6 py-3 text-sm',
    outline: 'inline-flex items-center justify-center font-body font-medium leading-relaxed tracking-extra-wide uppercase transition-all duration-300 focus:outline-none border px-6 py-3 text-sm',
    ghost: 'inline-flex items-center justify-center font-body font-medium leading-relaxed tracking-extra-wide uppercase transition-all duration-300 focus:outline-none px-6 py-3 text-sm',
    toggle: {
      base: 'py-4 font-body font-medium tracking-extra-wide uppercase transition-colors duration-300 text-center',
      active: '',
      inactive: 'bg-transparent transition-colors duration-300',
    },
  },
  nav: {
    base: 'font-body font-thin tracking-wide uppercase transition-colors duration-300',
    item: 'border-b',
    active: 'relative before:absolute before:inset-x-0 before:-top-1 before:h-1 before:border-t',
    inactive: 'transition-all duration-300 hover:relative hover:before:absolute hover:before:inset-x-0 hover:before:-top-1 hover:before:h-1 hover:before:border-t',
    icon: 'mr-4 flex-shrink-0 h-5 w-5 transition-all duration-300',
    iconActive: '',
    iconInactive: 'group-hover:scale-110',
    link: 'group flex items-center mx-4 px-6 py-4 text-sm border-b last:border-b-0 transition-all duration-300 uppercase',
  },
  badge: {
    primary: 'font-body font-semibold text-xs leading-none tracking-wider uppercase px-3 py-1 border rounded-full',
    secondary: 'font-body font-semibold text-xs leading-none tracking-wider uppercase px-3 py-1 border rounded-full',
    status: 'font-body font-semibold text-xs leading-none tracking-wider uppercase px-3 py-1 border rounded-full',
    value: 'font-body font-semibold text-xs leading-none tracking-wider uppercase px-3 py-1 border rounded-full',
    imageOverlay: 'absolute top-3 left-3 flex items-center gap-1.5 px-2.5 py-1.5 backdrop-blur-sm font-body font-medium text-xs tracking-wider uppercase z-10',
  },
  layout: {
    container: 'flex h-screen',
    mobileHeader: 'md:hidden fixed top-0 left-0 right-0 z-30 border-b',
    mobileButton: 'p-2 transition-colors focus:outline-none focus:ring-2 focus:ring-opacity-50 rounded',
    main: 'flex-1 relative overflow-y-auto focus:outline-none',
  },
  sidebar: {
    container: 'md:flex md:w-80 md:flex-col md:fixed md:inset-y-0 md:z-40 fixed inset-y-0 left-0 z-50 w-80 transform transition-transform duration-300 ease-in-out md:transform-none overflow-x-hidden',
    inner: 'flex-1 flex flex-col h-full overflow-x-hidden',
    header: 'flex items-center justify-center h-28 flex-shrink-0 px-8 border-b relative overflow-x-hidden',
    navigation: 'flex-1 flex flex-col py-4 overflow-y-auto overflow-x-hidden',
    footer: 'flex-shrink-0 mx-4 px-6 py-8',
  },
  common: {
    card: 'border transition-all duration-300',
    imageContainer: 'relative overflow-hidden',
    imageOverlay: 'absolute inset-0 bg-black bg-opacity-20 group-hover:bg-opacity-10 transition-all duration-300',
    placeholderGrid: 'grid grid-cols-2 gap-1',
    placeholderItem: 'w-3 h-3',
    metadataContainer: 'space-y-2',
    metadataItem: 'flex items-center space-x-2',
    metadataIcon: 'h-4 w-4 flex-shrink-0',
    contentContainer: 'p-4 space-y-3',
    badgeContainer: 'flex flex-wrap gap-2 items-center',
    titleContainer: 'line-clamp-3 transition-colors duration-300 break-words hyphens-auto',
    image: 'w-full h-full object-cover',
    placeholderCenter: 'w-full h-full flex items-center justify-center',
  },
  utilities: {
    input: 'border focus:ring-2 focus:ring-blue-500',
    checkbox: {
      base: 'w-4 h-4 border rounded focus:ring-1 focus:ring-offset-0',
      checked: '',
      unchecked: '',
    },
  },
  form: {
    input: 'w-full px-4 py-3 border focus:outline-none transition-colors font-body',
    textarea: 'w-full px-4 py-3 border focus:outline-none transition-colors font-body resize-none',
    select: 'w-full px-4 py-3 border focus:outline-none transition-colors font-body',
    label: 'block text-xs font-medium uppercase tracking-wider mb-2',
  },
  mobile: {
    backdrop: 'fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden',
    closeButton: 'absolute top-4 right-4 p-2 transition-colors md:hidden',
  },
  icon: {
    arrow: 'w-4 h-4 transition-all duration-300',
    arrowActive: 'translate-x-1',
    arrowHover: 'group-hover:translate-x-1',
  },
  animation: {
    imageHover: 'group-hover:scale-105 transition-transform duration-500',
    overlayHover: 'group-hover:bg-opacity-10 transition-all duration-300',
    cardHover: 'transition-all duration-300',
  },
  pageLayout: {
    benefitsGrid: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8',
    destinationsGrid: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4',
    giftGuidesGrid: 'grid grid-cols-1 md:grid-cols-2 gap-8',
    profileGrid: 'grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto items-start',
    destinationCard: 'group relative aspect-square cursor-pointer overflow-hidden',
    destinationImage: 'w-full h-full object-cover group-hover:scale-105 transition-transform duration-500',
    destinationOverlay: 'absolute inset-0 bg-black bg-opacity-20 group-hover:bg-opacity-10 transition-all duration-300',
    destinationNameBox: 'absolute inset-0 flex items-center justify-center px-2 py-4',
    destinationNameContainer: 'bg-black bg-opacity-70 border border-white px-2 py-4 min-w-[80%] max-w-[90%] text-center text-white break-words hyphens-auto overflow-wrap-anywhere',
    destinationPlaceholder: 'w-full h-full flex items-center justify-center',
    destinationPlaceholderGrid: 'grid grid-cols-2 gap-1',
    destinationPlaceholderItem: 'w-3 h-3',
    memberInfoCard: 'p-8',
    memberInfoProfile: 'flex items-start space-x-6 mb-8',
    memberInfoImage: 'relative flex-shrink-0',
    memberInfoImageContainer: 'cursor-pointer group',
    memberInfoImageOverlay: 'absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-all duration-200 rounded-lg flex items-center justify-center',
    memberInfoImageText: 'text-white text-center px-2',
    memberInfoImageSpinner: 'animate-spin rounded-full h-6 w-6 border-b-2 border-white mx-auto mb-1',
    memberInfoImageIcon: 'w-6 h-6 mx-auto mb-1',
    memberInfoImageLabel: 'text-xs font-medium',
    memberInfoContent: 'flex-1',
    memberInfoDetails: 'space-y-6',
    memberInfoDivider: 'pt-6 border-t',
    memberInfoContactCard: 'p-8 mt-8',
    memberInfoContactGrid: 'grid grid-cols-3 gap-4 items-start',
    memberInfoContactLabel: 'col-span-1',
    memberInfoContactValue: 'col-span-2 break-words',
    subscriptionCard: 'p-8',
    subscriptionStatusCard: 'p-4 border rounded-lg',
    subscriptionStatusIcon: 'h-5 w-5',
    subscriptionStatusText: 'text-sm',
    subscriptionStatusValue: 'text-xs mt-1',
    subscriptionBillingCard: 'p-4 border rounded-lg',
    subscriptionBillingIcon: 'h-5 w-5',
    subscriptionBillingText: 'text-sm',
    subscriptionBillingValue: 'text-xs mt-1',
    subscriptionButton: 'w-full flex items-center justify-center space-x-2',
    subscriptionButtonSpinner: 'animate-spin rounded-full h-4 w-4 border-b-2 border-current',
    subscriptionInfoText: 'text-center',
    subscriptionInfoDescription: 'text-sm',
    paymentManagementCard: 'backdrop-blur-sm border rounded-lg p-6',
    paymentManagementHeader: 'flex justify-between items-start mb-6',
    paymentManagementTitle: '',
    paymentManagementDescription: '',
    paymentManagementGrid: 'grid grid-cols-1 md:grid-cols-2 gap-6',
    paymentManagementLabel: '',
    paymentManagementValue: '',
    paymentManagementStatusCard: 'border rounded-lg p-4',
    paymentManagementStatusIcon: 'w-5 h-5 mt-0.5 flex-shrink-0',
    paymentManagementButton: 'group border bg-transparent py-3 px-8 font-body font-medium tracking-wide uppercase transition-all duration-300 flex items-center space-x-4 disabled:opacity-50 disabled:cursor-not-allowed',
    paymentManagementButtonSpinner: 'animate-spin rounded-full h-4 w-4 border-b-2 border-current',
    paymentManagementButtonIcon: 'w-4 h-4 group-hover:translate-x-1 transition-transform duration-300',
    paymentManagementError: 'text-center',
    sectionSpacing: 'space-y-20',
    categorySpacing: 'space-y-12',
    loadingSpinner: 'animate-spin rounded-full h-12 w-12 border-b-2 mx-auto',
    errorBanner: 'border p-4 text-center',
    dateInputDark: '[&::-webkit-calendar-picker-indicator]:filter-invert [&::-webkit-calendar-picker-indicator]:brightness-0 [&::-webkit-calendar-picker-indicator]:invert',
    dateInputLight: '[&::-webkit-calendar-picker-indicator]:filter-none',
  },
};

// Dark theme - uses semantic theme colors (CSS variables)
export const themeClasses = {
  // Layout
  page: `min-h-screen font-body font-thin tracking-wide bg-theme-bg text-theme-text`,

  // Typography - base + semantic colors
  title: {
    h1: `${baseTheme.title.h1} text-theme-text`,
    h2: `${baseTheme.title.h2} text-theme-text`,
    h3: `${baseTheme.title.h3} text-theme-text`,
    h4: `${baseTheme.title.h4} text-theme-text`,
    h5: `${baseTheme.title.h5} text-theme-text`,
  },
  text: {
    body: `${baseTheme.text.body} text-theme-text`,
    field: `${baseTheme.text.field} text-theme-text`,
    muted: `${baseTheme.text.muted} text-theme-text-muted`,
    mutedSmall: `${baseTheme.text.mutedSmall} text-theme-text-muted`,
    breadcrumb: `${baseTheme.text.breadcrumb} text-theme-text-muted`,
    ultraWide: `${baseTheme.text.ultraWide} text-theme-text`,
    primary: `${baseTheme.text.primary} text-theme-text`,
    secondary: `${baseTheme.text.secondary} text-theme-text-secondary`,
    caption: `${baseTheme.text.caption} text-theme-text-muted`,
    button: baseTheme.text.button,
    sectionHeader: `${baseTheme.text.sectionHeader} text-theme-text`,
    pageTitle: `${baseTheme.text.pageTitle} text-theme-text`,
    stepTitle: `${baseTheme.text.stepTitle} text-theme-text`,
  },

  // Colors - Core color system (semantic)
  colors: {
    bg: {
      primary: 'bg-theme-bg',
      secondary: 'bg-theme-bg-secondary',
      card: 'bg-theme-bg-card',
      white: 'bg-theme-accent',
    },
    text: {
      primary: 'text-theme-text',
      secondary: 'text-theme-text-secondary',
      muted: 'text-theme-text-muted',
      mutedSmall: 'text-theme-text-muted',
    },
    border: {
      primary: 'border-theme-border',
      secondary: 'border-theme-border-secondary',
      accent: 'border-theme-border-accent',
    },
    hover: {
      bg: 'hover:bg-theme-bg-hover',
      text: 'hover:text-theme-text',
      border: 'hover:border-theme-border-accent',
    }
  },

  // Components - base + semantic colors
  card: {
    base: `bg-theme-bg-card border border-theme-border`,
    hover: `hover:bg-theme-bg-hover transition-colors duration-300`,
  },

  common: {
    ...baseTheme.common,
    card: `bg-theme-bg-card border-theme-border hover:border-theme-border-accent ${baseTheme.common.card}`,
    imageContainer: `${baseTheme.common.imageContainer} bg-theme-bg-secondary`,
    placeholderItem: `${baseTheme.common.placeholderItem} bg-theme-border-secondary`,
  },

  button: {
    primary: `${baseTheme.button.primary} bg-theme-accent text-theme-accent-text hover:opacity-90 hover:scale-105`,
    secondary: `${baseTheme.button.secondary} border-theme-border-accent text-theme-text hover:bg-theme-accent hover:text-theme-accent-text hover:scale-105`,
    outline: `${baseTheme.button.outline} border-theme-border text-theme-text hover:bg-theme-accent hover:text-theme-accent-text hover:border-theme-border-accent hover:scale-105`,
    ghost: `${baseTheme.button.ghost} text-theme-text hover:text-theme-text-secondary hover:scale-105`,
    toggle: {
      base: baseTheme.button.toggle.base,
      active: `bg-theme-accent text-theme-accent-text ${baseTheme.button.toggle.active}`,
      inactive: `text-theme-text hover:bg-theme-accent hover:text-theme-accent-text ${baseTheme.button.toggle.inactive}`,
    },
  },

  nav: {
    ...baseTheme.nav,
    item: `${baseTheme.nav.item} text-theme-text hover:bg-theme-bg-hover`,
    active: `${baseTheme.nav.active} bg-theme-bg-active text-theme-text border-theme-border-accent before:bg-theme-bg-active before:border-theme-border-accent`,
    inactive: `${baseTheme.nav.inactive} text-theme-text hover:bg-theme-bg-hover hover:text-theme-text hover:before:bg-theme-bg-hover hover:before:border-theme-bg-hover`,
    iconActive: `${baseTheme.nav.iconActive} text-theme-text`,
    iconInactive: `${baseTheme.nav.iconInactive} text-theme-text-muted group-hover:text-theme-text`,
  },

  badge: {
    primary: `${baseTheme.badge.primary} text-theme-text-secondary border-theme-border-secondary bg-theme-bg-card`,
    secondary: `${baseTheme.badge.secondary} text-theme-text-muted border-theme-border bg-theme-bg`,
    status: `${baseTheme.badge.status} text-theme-text-muted border-theme-border bg-theme-bg`,
    value: `${baseTheme.badge.value} text-theme-text-secondary border-theme-border-secondary bg-theme-bg-card`,
    imageOverlay: `${baseTheme.badge.imageOverlay} bg-black/60 border border-white/20 text-white`,
  },

  layout: {
    ...baseTheme.layout,
    container: `${baseTheme.layout.container} bg-theme-bg`,
    mobileHeader: `${baseTheme.layout.mobileHeader} bg-theme-bg border-theme-border`,
    mobileButton: `${baseTheme.layout.mobileButton} text-theme-text hover:text-theme-text-secondary focus:ring-theme-accent`,
    main: `${baseTheme.layout.main} bg-theme-bg`,
  },

  sidebar: baseTheme.sidebar,

  // Utilities - base + semantic colors
  input: `bg-theme-bg-secondary border-theme-border ${baseTheme.utilities.input}`,
  checkbox: {
    base: `${baseTheme.utilities.checkbox.base} border-theme-border focus:ring-theme-accent`,
    checked: `bg-theme-accent border-theme-accent text-theme-accent-text ${baseTheme.utilities.checkbox.checked}`,
    unchecked: `bg-theme-bg-secondary border-theme-border ${baseTheme.utilities.checkbox.unchecked}`,
  },

  // Form elements with theme colors
  form: {
    input: `${baseTheme.form.input} bg-theme-bg-secondary border-theme-border text-theme-text placeholder:text-theme-text-muted focus:border-theme-text`,
    textarea: `${baseTheme.form.textarea} bg-theme-bg-secondary border-theme-border text-theme-text placeholder:text-theme-text-muted focus:border-theme-text`,
    select: `${baseTheme.form.select} bg-theme-bg-secondary border-theme-border text-theme-text focus:border-theme-text`,
    label: `${baseTheme.form.label} text-theme-text-muted`,
  },

  mobile: {
    ...baseTheme.mobile,
    closeButton: baseTheme.mobile.closeButton,
  },

  icon: baseTheme.icon,

  animation: {
    ...baseTheme.animation,
    cardHover: `${baseTheme.animation.cardHover} hover:border-theme-border-accent`,
  },

  // Page layouts - base + semantic colors where needed
  pageLayout: {
    ...baseTheme.pageLayout,
    paymentManagementTitle: `${baseTheme.pageLayout.paymentManagementTitle} text-theme-text`,
    paymentManagementDescription: `${baseTheme.pageLayout.paymentManagementDescription} text-theme-text-secondary`,
    paymentManagementLabel: `${baseTheme.pageLayout.paymentManagementLabel} text-theme-text-muted`,
    paymentManagementValue: `${baseTheme.pageLayout.paymentManagementValue} text-theme-text`,
    paymentManagementButton: `${baseTheme.pageLayout.paymentManagementButton} border-theme-border-accent text-theme-text hover:bg-theme-accent hover:text-theme-accent-text`,
  },

  // Shared layout (same for both themes)
  ...sharedLayout,
};

// Light theme - uses same semantic theme colors (CSS variables handle the actual colors)
export const lightThemeClasses = {
  // Layout
  page: `min-h-screen font-body font-thin tracking-wide bg-theme-bg text-theme-text`,

  // Typography - base + semantic colors
  title: {
    h1: `${baseTheme.title.h1} text-theme-text`,
    h2: `${baseTheme.title.h2} text-theme-text`,
    h3: `${baseTheme.title.h3} text-theme-text`,
    h4: `${baseTheme.title.h4} text-theme-text`,
    h5: `${baseTheme.title.h5} text-theme-text`,
  },
  text: {
    body: `${baseTheme.text.body} text-theme-text`,
    field: `${baseTheme.text.field} text-theme-text`,
    muted: `${baseTheme.text.muted} text-theme-text-muted`,
    mutedSmall: `${baseTheme.text.mutedSmall} text-theme-text-muted`,
    breadcrumb: `${baseTheme.text.breadcrumb} text-theme-text-muted`,
    ultraWide: `${baseTheme.text.ultraWide} text-theme-text`,
    primary: `${baseTheme.text.primary} text-theme-text`,
    secondary: `${baseTheme.text.secondary} text-theme-text-secondary`,
    caption: `${baseTheme.text.caption} text-theme-text-muted`,
    button: baseTheme.text.button,
    sectionHeader: `${baseTheme.text.sectionHeader} text-theme-text`,
    pageTitle: `${baseTheme.text.pageTitle} text-theme-text`,
    stepTitle: `${baseTheme.text.stepTitle} text-theme-text`,
  },

  // Colors - Core color system (semantic)
  colors: {
    bg: {
      primary: 'bg-theme-bg',
      secondary: 'bg-theme-bg-secondary',
      card: 'bg-theme-bg-card',
      white: 'bg-theme-accent',
    },
    text: {
      primary: 'text-theme-text',
      secondary: 'text-theme-text-secondary',
      muted: 'text-theme-text-muted',
      mutedSmall: 'text-theme-text-muted',
    },
    border: {
      primary: 'border-theme-border',
      secondary: 'border-theme-border-secondary',
      accent: 'border-theme-border-accent',
    },
    hover: {
      bg: 'hover:bg-theme-bg-hover',
      text: 'hover:text-theme-text',
      border: 'hover:border-theme-border-accent',
    }
  },

  // Components - base + semantic colors
  card: {
    base: `bg-theme-bg-card border border-theme-border`,
    hover: `hover:bg-theme-bg-hover transition-colors duration-300`,
  },

  common: {
    ...baseTheme.common,
    card: `bg-theme-bg-card border-theme-border hover:border-theme-border-accent ${baseTheme.common.card}`,
    imageContainer: `${baseTheme.common.imageContainer} bg-theme-bg-secondary`,
    placeholderItem: `${baseTheme.common.placeholderItem} bg-theme-border-secondary`,
  },

  button: {
    primary: `${baseTheme.button.primary} bg-theme-accent text-theme-accent-text hover:opacity-90 hover:scale-105`,
    secondary: `${baseTheme.button.secondary} border-theme-border-accent text-theme-text hover:bg-theme-accent hover:text-theme-accent-text hover:scale-105`,
    outline: `${baseTheme.button.outline} border-theme-border text-theme-text hover:bg-theme-accent hover:text-theme-accent-text hover:border-theme-border-accent hover:scale-105`,
    ghost: `${baseTheme.button.ghost} text-theme-text hover:text-theme-text-secondary hover:scale-105`,
    toggle: {
      base: baseTheme.button.toggle.base,
      active: `bg-theme-accent text-theme-accent-text ${baseTheme.button.toggle.active}`,
      inactive: `text-theme-text hover:bg-theme-accent hover:text-theme-accent-text ${baseTheme.button.toggle.inactive}`,
    },
  },

  nav: {
    ...baseTheme.nav,
    item: `${baseTheme.nav.item} text-theme-text hover:bg-theme-bg-hover`,
    active: `${baseTheme.nav.active} bg-theme-bg-active text-theme-text border-theme-border-accent before:bg-theme-bg-active before:border-theme-border-accent`,
    inactive: `${baseTheme.nav.inactive} text-theme-text hover:bg-theme-bg-hover hover:text-theme-text hover:before:bg-theme-bg-hover hover:before:border-theme-bg-hover`,
    iconActive: `${baseTheme.nav.iconActive} text-theme-text`,
    iconInactive: `${baseTheme.nav.iconInactive} text-theme-text-muted group-hover:text-theme-text`,
  },

  badge: {
    primary: `${baseTheme.badge.primary} text-theme-text-secondary border-theme-border-secondary bg-theme-bg-card`,
    secondary: `${baseTheme.badge.secondary} text-theme-text-muted border-theme-border bg-theme-bg`,
    status: `${baseTheme.badge.status} text-theme-text-muted border-theme-border bg-theme-bg`,
    value: `${baseTheme.badge.value} text-theme-text-secondary border-theme-border-secondary bg-theme-bg-card`,
    imageOverlay: `${baseTheme.badge.imageOverlay} bg-white/60 border border-gray-400/20 text-gray-900`,
  },

  layout: {
    ...baseTheme.layout,
    container: `${baseTheme.layout.container} bg-theme-bg`,
    mobileHeader: `${baseTheme.layout.mobileHeader} bg-theme-bg border-theme-border`,
    mobileButton: `${baseTheme.layout.mobileButton} text-theme-text hover:text-theme-text-secondary focus:ring-theme-accent`,
    main: `${baseTheme.layout.main} bg-theme-bg`,
  },

  sidebar: baseTheme.sidebar,

  // Utilities - base + semantic colors
  input: `bg-theme-bg-card border-theme-border ${baseTheme.utilities.input}`,
  checkbox: {
    base: `${baseTheme.utilities.checkbox.base} border-theme-border focus:ring-theme-accent appearance-none cursor-pointer`,
    checked: `bg-theme-accent border-theme-accent text-theme-accent-text ${baseTheme.utilities.checkbox.checked}`,
    unchecked: `bg-theme-bg-card border-theme-border ${baseTheme.utilities.checkbox.unchecked}`,
  },

  mobile: {
    ...baseTheme.mobile,
    closeButton: baseTheme.mobile.closeButton,
  },

  icon: baseTheme.icon,

  animation: {
    ...baseTheme.animation,
    cardHover: `${baseTheme.animation.cardHover} hover:border-theme-border-accent`,
  },

  // Page layouts - base + semantic colors where needed
  pageLayout: {
    ...baseTheme.pageLayout,
    paymentManagementTitle: `${baseTheme.pageLayout.paymentManagementTitle} text-theme-text`,
    paymentManagementDescription: `${baseTheme.pageLayout.paymentManagementDescription} text-theme-text-secondary`,
    paymentManagementLabel: `${baseTheme.pageLayout.paymentManagementLabel} text-theme-text-muted`,
    paymentManagementValue: `${baseTheme.pageLayout.paymentManagementValue} text-theme-text`,
    paymentManagementButton: `${baseTheme.pageLayout.paymentManagementButton} border-theme-border-accent text-theme-text hover:bg-theme-accent hover:text-theme-accent-text`,
  },

  // Shared layout (same for both themes)
  ...sharedLayout,
};

// Get theme classes based on member type
export const getThemeClassesForMemberType = (memberType: string) => {
  const tier = normalizeMemberType(memberType);

  // Light theme tiers: explorer, program
  // Dark theme tiers: core, elevate, executive, prestige
  if (LIGHT_THEME_USER_TYPES.includes(tier)) {
    return lightThemeClasses;
  } else {
    return themeClasses;
  }
};

// Logo selection utilities
export const getLogoForMemberType = (
  memberType: string,
  recordType?: string | null,
  corporatePartnerBranding?: CorporatePartnerBranding | null
): string => {
  // Four Hundred Advisor Network — header uses FHAN artwork (deploy /images/fhan-logo.png or override via hosting).
  if (recordType === 'Advisor_Account') {
    return '/images/fhan-logo.png';
  }

  // For Corporate Accounts, check the corporate tier
  if (recordType === 'Corporate_Account') {
    const corporateTier = corporatePartnerBranding?.corporateTier?.toLowerCase();

    // Executive tier corporate partners get standard FH Core look (no custom branding)
    if (corporateTier === 'executive') {
      return '/images/fh-logo-white.png'; // White logo for dark theme
    }

    // Other tiers (prestige, legacy, etc.) use custom branding if available
    if (corporatePartnerBranding) {
      const hasLightBackground = isLightColor(corporatePartnerBranding.backgroundColor);
      return hasLightBackground ? corporatePartnerBranding.logoDark : corporatePartnerBranding.logoLight;
    }

    // Fallback for corporate accounts without branding loaded yet
    return '/images/fh-logo-white.png';
  }

  // For Membership Accounts, use member type to determine logo
  const tier = normalizeMemberType(memberType);
  const isLightTheme = LIGHT_THEME_USER_TYPES.includes(tier);

  switch (tier) {
    case 'explorer':
      return isLightTheme ? '/logos/Explorer/FH-Explorer-Logo_Black.png' : '/logos/Explorer/FH-Membership-Tier-Logo-White.png';
    case 'core':
    case 'executive_circle': // Executive Circle looks same as Core
      return isLightTheme ? '/images/fh-logo-dark.png' : '/images/fh-logo-white.png';
    case 'elevate':
      return isLightTheme ? '/logos/Elevate/FH-Elevate-Logo-Black.png' : '/logos/Elevate/FH-Elevate-Logo-White.png';
    default:
      return isLightTheme ? '/images/fh-logo-dark.png' : '/images/fh-logo-white.png';
  }
};
